import React, { useEffect, useState } from 'react';
import { apiCall, apiUrl } from '../utils/api';

interface Song {
  id: number;
  title: string;
  artist: string;
  genre: string;
  pdf_path: string | null;
  lyrics: string | null;
  lyrics_validated?: boolean;
  vocal_type?: string | null;
  has_lyrics?: boolean;
  created_at?: string;
  updated_at?: string;
  total_plays?: number;
  completed_plays?: number;
  unique_users?: number;
  last_played?: string | null;
  votes?: {
    total_votes: number;
    average_rating: number;
  };
}

interface SongStats {
  total_plays: number;
  completed_plays: number;
  skipped_plays: number;
  expired_plays: number;
  unique_users: number;
  last_played: string | null;
  first_queued: string | null;
  users: Array<{
    user_name: string;
    play_count: number;
    last_played: string | null;
  }>;
  votes?: {
    total_votes: number;
    average_rating: number;
  };
}

interface VoteStats {
  aggregate: {
    total_votes: number;
    average_rating: number;
    unique_voters: number;
    one_star: number;
    two_star: number;
    three_star: number;
    four_star: number;
    five_star: number;
    comments_count: number;
  };
  monthlyTrends: Array<{
    month: string;
    vote_count: number;
    average_rating: number;
  }>;
  recentComments: Array<{
    voter_name: string;
    rating: number;
    comment: string;
    vote_date: string;
  }>;
}

interface VoteHistoryItem {
  id: number;
  voter_name: string;
  rating: number;
  comment: string | null;
  created_at: string;
  performance_date: string;
  performer_name: string | null;
  song_history_id: number | null;
  gig_session: string | null;
  completed_at: string | null;
}

/* ── design tokens ── */
const T = {
  cardBg:       'rgba(40,25,15,0.95)',
  border:       '#5a3a1a',
  text:         '#f5deb3',
  textSecondary:'#a08060',
  textMuted:    '#806040',
  gold:         '#ffd700',
  amber:        '#c8850f',
  inputBg:      '#28190f',
  inputBorder:  '#5a3a1a',
  inputFocus:   '#c8850f',
  btnPrimary:   'linear-gradient(180deg, #c8850f 0%, #8b5a0a 100%)',
  btnSecondary: '#5a3a1a',
  btnDanger:    '#e74c3c',
  successBg:    'rgba(46,204,113,0.15)',
  successText:  '#2ecc71',
  successBorder:'rgba(46,204,113,0.3)',
  errorBg:      'rgba(231,76,60,0.15)',
  errorText:    '#e74c3c',
  overlayBg:    'rgba(0,0,0,0.7)',
  genreBadgeBg: 'rgba(90,58,26,0.6)',
  genreBadgeText:'#c8a060',
  tableRowEven: 'rgba(40,25,15,0.6)',
  tableRowOdd:  'rgba(30,18,10,0.6)',
  tableHeaderBg:'rgba(30,18,10,0.9)',
} as const;

/* shared style fragments */
const inputStyle: React.CSSProperties = {
  backgroundColor: T.inputBg,
  border: `1px solid ${T.inputBorder}`,
  color: T.text,
  borderRadius: '4px',
  outline: 'none',
};

const btnBase: React.CSSProperties = {
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontWeight: 600,
  letterSpacing: '0.02em',
};

// Lead-vocal classification. Mirrors VALID_VOCAL_TYPES in server/src/routes/songs.ts.
const VOCAL_TYPES = [
  { value: '', label: 'Unclassified' },
  { value: 'cowboy', label: 'Cowboy (male lead)' },
  { value: 'cowgirl', label: 'Cowgirl (female lead)' },
  { value: 'duet', label: 'Duet' },
];

const SongAdminPage: React.FC = () => {
  const [songs, setSongs] = useState<Song[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGenre, setFilterGenre] = useState('');
  const [showStats, setShowStats] = useState(false);
  const [chartsOnly, setChartsOnly] = useState(false);
  const [editingSong, setEditingSong] = useState<Song | null>(null);
  const [creatingSong, setCreatingSong] = useState(false);
  const [uploadingChart, setUploadingChart] = useState<number | null>(null);
  const [uploadingInstrument, setUploadingInstrument] = useState<{ songId: number; instrument: string } | null>(null);
  const [viewingStats, setViewingStats] = useState<number | null>(null);
  const [songStats, setSongStats] = useState<SongStats | null>(null);
  const [voteStats, setVoteStats] = useState<VoteStats | null>(null);
  const [viewingVoteHistory, setViewingVoteHistory] = useState<number | null>(null);
  const [voteHistory, setVoteHistory] = useState<VoteHistoryItem[]>([]);
  const [voteHistoryLoading, setVoteHistoryLoading] = useState(false);
  const [voteHistoryTotal, setVoteHistoryTotal] = useState(0);
  const [voteHistoryOffset, setVoteHistoryOffset] = useState(0);
  const [voteHistoryFilters, setVoteHistoryFilters] = useState({
    startDate: '',
    endDate: '',
    minRating: '',
    maxRating: '',
    hasComment: false,
    search: ''
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [genres, setGenres] = useState<string[]>([]);
  const [validationReport, setValidationReport] = useState<any>(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [viewingChart, setViewingChart] = useState<{ songId: number; title: string; artist: string; slug: string; instrument: string; v: number } | null>(null);
  const [chartDropdownSongId, setChartDropdownSongId] = useState<number | null>(null);
  const [editingLyrics, setEditingLyrics] = useState<{
    song: Song;
    lyrics: string;
    saving: boolean;
    regenerating: boolean;
    pdfVersion: number;
    referenceInstrument: string | null;
    justSaved: boolean;
  } | null>(null);

  const INSTRUMENTS = [
    { value: 'full', label: 'Full Band' },
    { value: 'bass', label: 'Bass' },
    { value: 'guitar1', label: 'Guitar 1' },
    { value: 'guitar1_capo', label: 'Guitar 1 (Capo)' },
    { value: 'guitar2', label: 'Guitar 2' },
    { value: 'guitar2_capo', label: 'Guitar 2 (Capo)' },
    { value: 'keys', label: 'Keys' },
    { value: 'drums', label: 'Drums' },
    { value: 'fiddle', label: 'Fiddle / Mandolin' },
  ];

  useEffect(() => {
    fetchSongs();
  }, [showStats]);

  const fetchSongs = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = showStats
        ? '/api/songs?includeStats=true'
        : '/api/songs';
      const res = await apiCall(url);
      if (!res.ok) throw new Error('Failed to fetch songs');
      const data = await res.json();
      setSongs(data);

      // Extract unique genres
      const uniqueGenres = Array.from(new Set(data.map((s: Song) => s.genre).filter(Boolean))) as string[];
      setGenres(uniqueGenres.sort());
    } catch (err: any) {
      setError(err.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSongStats = async (songId: number) => {
    try {
      const res = await apiCall(`/api/songs/${songId}/stats`);
      if (!res.ok) throw new Error('Failed to fetch stats');
      const data = await res.json();
      setSongStats(data);

      // Also fetch vote statistics
      const voteRes = await apiCall(`/api/songs/${songId}/vote-stats`);
      if (voteRes.ok) {
        const voteData = await voteRes.json();
        setVoteStats(voteData);
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Failed to load statistics' });
    }
  };

  const fetchVoteHistory = async (songId: number, offset: number = 0) => {
    setVoteHistoryLoading(true);
    try {
      const params = new URLSearchParams({
        limit: '50',
        offset: offset.toString(),
        ...(voteHistoryFilters.startDate && { startDate: voteHistoryFilters.startDate }),
        ...(voteHistoryFilters.endDate && { endDate: voteHistoryFilters.endDate }),
        ...(voteHistoryFilters.minRating && { minRating: voteHistoryFilters.minRating }),
        ...(voteHistoryFilters.maxRating && { maxRating: voteHistoryFilters.maxRating }),
        ...(voteHistoryFilters.hasComment && { hasComment: 'true' }),
        ...(voteHistoryFilters.search && { search: voteHistoryFilters.search }),
      });

      const res = await apiCall(`/api/songs/${songId}/vote-history?${params}`);
      if (!res.ok) throw new Error('Failed to fetch vote history');
      const data = await res.json();
      setVoteHistory(data.votes);
      setVoteHistoryTotal(data.total);
      setVoteHistoryOffset(offset);
    } catch (err: any) {
      setMessage({ type: 'error', text: 'Failed to load vote history' });
    } finally {
      setVoteHistoryLoading(false);
    }
  };

  const handleEdit = (song: Song) => {
    setEditingSong({ ...song });
    setCreatingSong(false);
  };

  const fetchValidation = async () => {
    setValidationLoading(true);
    try {
      const res = await apiCall('/api/songs/validate');
      if (res.ok) {
        const data = await res.json();
        setValidationReport(data);
      }
    } catch (err) {
      console.error('Failed to fetch validation report:', err);
    } finally {
      setValidationLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingSong({
      id: 0,
      title: '',
      artist: '',
      genre: 'Country',
      pdf_path: null,
      lyrics: null
    });
    setCreatingSong(true);
  };

  const handleSave = async () => {
    if (!editingSong) return;

    if (!editingSong.title || !editingSong.artist) {
      setMessage({ type: 'error', text: 'Title and Artist are required' });
      return;
    }

    try {
      const url = creatingSong
        ? '/api/songs'
        : `/api/songs/${editingSong.id}`;
      const method = creatingSong ? 'POST' : 'PUT';

      const res = await apiCall(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editingSong.title,
          artist: editingSong.artist,
          genre: editingSong.genre,
          lyrics: editingSong.lyrics || null,
          pdf_path: editingSong.pdf_path || null,
          vocal_type: editingSong.vocal_type ?? ''
        })
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to save song');
      }

      setMessage({ type: 'success', text: creatingSong ? 'Song created!' : 'Song updated!' });
      setEditingSong(null);
      setCreatingSong(false);
      fetchSongs();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to save song' });
    }
  };

  const handleDelete = async (songId: number) => {
    if (!confirm('Are you sure you want to delete this song? This action cannot be undone.')) {
      return;
    }

    try {
      const res = await apiCall(`/api/songs/${songId}`, {
        method: 'DELETE'
      });

      if (!res.ok) throw new Error('Failed to delete song');

      setMessage({ type: 'success', text: 'Song deleted!' });
      fetchSongs();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to delete song' });
    }
  };

  const handleChartUpload = async (songId: number, file: File, instrument: string = '') => {
    setUploadingChart(songId);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('chart', file);
      if (instrument) {
        formData.append('instrument', instrument);
      }

      const url = apiUrl(`/api/songs/${songId}/upload-chart`);
      const res = await fetch(url, {
        method: 'POST',
        body: formData
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to upload chart');
      }

      const data = await res.json();
      const instrumentLabel = instrument ? INSTRUMENTS.find(i => i.value === instrument)?.label || instrument : 'Base chart';
      setMessage({ type: 'success', text: `${instrumentLabel} uploaded successfully!` });
      fetchSongs();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to upload chart' });
    } finally {
      setUploadingChart(null);
      setUploadingInstrument(null);
    }
  };

  const handleViewStats = async (songId: number) => {
    setViewingStats(songId);
    setViewingVoteHistory(null);
    await fetchSongStats(songId);
  };

  const handleViewVoteHistory = async (songId: number) => {
    setViewingVoteHistory(songId);
    setVoteHistoryOffset(0);
    await fetchVoteHistory(songId, 0);
  };

  const handleEditLyrics = (song: Song) => {
    setEditingLyrics({
      song,
      lyrics: song.lyrics || '',
      saving: false,
      regenerating: false,
      pdfVersion: Date.now(),
      referenceInstrument: null,
      justSaved: false,
    });
  };

  const handleSaveLyricsAndRegenerate = async () => {
    if (!editingLyrics) return;
    const { song, lyrics } = editingLyrics;

    setEditingLyrics(prev => prev ? { ...prev, saving: true } : null);

    try {
      const saveRes = await apiCall(`/api/songs/${song.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lyrics }),
      });
      if (!saveRes.ok) {
        const err = await saveRes.json();
        throw new Error(err.error || 'Failed to save lyrics');
      }

      setEditingLyrics(prev => prev ? { ...prev, saving: false, regenerating: true } : null);

      const regenRes = await apiCall(`/api/songs/${song.id}/regenerate-vocals`, {
        method: 'POST',
      });
      if (!regenRes.ok) {
        const err = await regenRes.json();
        throw new Error(err.error || 'Failed to regenerate vocals PDF');
      }

      setMessage({ type: 'success', text: `Lyrics saved and vocals PDF regenerated for "${song.title}"` });
      setEditingLyrics(prev => prev ? { ...prev, saving: false, regenerating: false, pdfVersion: Date.now(), justSaved: true } : null);
      // Flash a transient "saved" acknowledgement next to the validate button, then clear it.
      setTimeout(() => setEditingLyrics(prev => prev ? { ...prev, justSaved: false } : null), 2600);
      // Optimistically update the row so lyrics status reflects instantly
      setSongs(prev => prev.map(s => s.id === song.id ? { ...s, lyrics, has_lyrics: !!(lyrics && lyrics.trim().length > 20) } : s));
      fetchSongs();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to save lyrics' });
      setEditingLyrics(prev => prev ? { ...prev, saving: false, regenerating: false } : null);
    }
  };

  const handleSaveLyricsOnly = async () => {
    if (!editingLyrics) return;
    const { song, lyrics } = editingLyrics;

    setEditingLyrics(prev => prev ? { ...prev, saving: true } : null);

    try {
      const saveRes = await apiCall(`/api/songs/${song.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lyrics }),
      });
      if (!saveRes.ok) {
        const err = await saveRes.json();
        throw new Error(err.error || 'Failed to save lyrics');
      }

      setMessage({ type: 'success', text: `Lyrics saved for "${song.title}"` });
      setEditingLyrics(null);
      // Optimistically update the row so lyrics status reflects instantly
      setSongs(prev => prev.map(s => s.id === song.id ? { ...s, lyrics, has_lyrics: !!(lyrics && lyrics.trim().length > 20) } : s));
      fetchSongs();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to save lyrics' });
      setEditingLyrics(prev => prev ? { ...prev, saving: false } : null);
    }
  };

  const handleToggleLyricsValidated = async () => {
    if (!editingLyrics) return;
    const { song } = editingLyrics;
    const newValue = !song.lyrics_validated;
    try {
      const res = await apiCall(`/api/songs/${song.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lyrics_validated: newValue }),
      });
      if (!res.ok) throw new Error('Failed to update validation status');
      setEditingLyrics(prev => prev ? { ...prev, song: { ...prev.song, lyrics_validated: newValue } } : null);
      // Optimistically update the row so the list reflects the change instantly
      setSongs(prev => prev.map(s => s.id === song.id ? { ...s, lyrics_validated: newValue } : s));
      setMessage({ type: 'success', text: `Lyrics ${newValue ? 'validated' : 'unvalidated'} for "${song.title}"` });
      fetchSongs();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const filteredSongs = songs.filter(song => {
    const matchesSearch =
      song.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      song.artist.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGenre = !filterGenre || song.genre === filterGenre;
    const matchesCharts = !chartsOnly || (song as any).chart_count > 0;
    return matchesSearch && matchesGenre && matchesCharts;
  });

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="container" style={{ padding: '20px', background: 'transparent', color: T.text }}>
      <h1 style={{ color: T.gold, marginBottom: '24px', fontWeight: 700, letterSpacing: '0.03em' }}>
        Song Administration
      </h1>

      {/* ── alert messages ── */}
      {message && (
        <div style={{
          padding: '12px 16px',
          marginBottom: '20px',
          backgroundColor: message.type === 'success' ? T.successBg : T.errorBg,
          color: message.type === 'success' ? T.successText : T.errorText,
          border: `1px solid ${message.type === 'success' ? T.successBorder : 'rgba(231,76,60,0.3)'}`,
          borderRadius: '6px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <span>{message.text}</span>
          <button
            onClick={() => setMessage(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: message.type === 'success' ? T.successText : T.errorText,
              fontSize: '18px',
              lineHeight: 1,
            }}
          >
            x
          </button>
        </div>
      )}

      {/* ── toolbar ── */}
      <div style={{
        marginBottom: '20px',
        display: 'flex',
        gap: '10px',
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        <input
          type="text"
          placeholder="Search songs..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{
            ...inputStyle,
            padding: '8px 12px',
            fontSize: '14px',
            flex: '1',
            minWidth: '200px',
          }}
        />
        <select
          value={filterGenre}
          onChange={e => setFilterGenre(e.target.value)}
          style={{
            ...inputStyle,
            padding: '8px 12px',
            fontSize: '14px',
          }}
        >
          <option value="">All Genres</option>
          {genres.map(genre => (
            <option key={genre} value={genre}>{genre}</option>
          ))}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', color: T.textSecondary, fontSize: '14px' }}>
          <input
            type="checkbox"
            checked={showStats}
            onChange={e => setShowStats(e.target.checked)}
            style={{ accentColor: T.amber }}
          />
          Show Statistics
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', color: T.textSecondary, fontSize: '14px' }}>
          <input
            type="checkbox"
            checked={chartsOnly}
            onChange={e => setChartsOnly(e.target.checked)}
            style={{ accentColor: T.amber }}
          />
          Charts Only
        </label>
        <button
          onClick={handleCreate}
          style={{
            ...btnBase,
            padding: '8px 16px',
            background: T.btnPrimary,
            color: '#fff',
            fontSize: '14px',
          }}
        >
          + Create New Song
        </button>
        <button
          onClick={fetchValidation}
          disabled={validationLoading}
          style={{
            ...btnBase,
            padding: '8px 16px',
            background: 'linear-gradient(180deg, #5a3a1a 0%, #3a2510 100%)',
            color: T.amber,
            border: `1px solid ${T.border}`,
            fontSize: '14px',
          }}
        >
          {validationLoading ? 'Running...' : 'Data Report'}
        </button>
      </div>

      {loading && <p style={{ color: T.textSecondary }}>Loading songs...</p>}
      {error && <p style={{ color: T.errorText }}>{error}</p>}

      {/* ── edit / create form ── */}
      {(editingSong || creatingSong) && (
        <div style={{
          border: `2px solid ${T.amber}`,
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '20px',
          backgroundColor: T.cardBg,
        }}>
          <h2 style={{ color: T.gold, marginTop: 0 }}>
            {creatingSong ? 'Create New Song' : 'Edit Song'}
          </h2>
          <div style={{ display: 'grid', gap: '14px', maxWidth: '600px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: T.textSecondary }}>
                Title *
              </label>
              <input
                type="text"
                value={editingSong?.title || ''}
                onChange={e => editingSong && setEditingSong({ ...editingSong, title: e.target.value })}
                style={{ ...inputStyle, width: '100%', padding: '8px 12px', fontSize: '14px', boxSizing: 'border-box' }}
                placeholder="Song title"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: T.textSecondary }}>
                Artist *
              </label>
              <input
                type="text"
                value={editingSong?.artist || ''}
                onChange={e => editingSong && setEditingSong({ ...editingSong, artist: e.target.value })}
                style={{ ...inputStyle, width: '100%', padding: '8px 12px', fontSize: '14px', boxSizing: 'border-box' }}
                placeholder="Artist name"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: T.textSecondary }}>
                Genre
              </label>
              <input
                type="text"
                value={editingSong?.genre || ''}
                onChange={e => editingSong && setEditingSong({ ...editingSong, genre: e.target.value })}
                style={{ ...inputStyle, width: '100%', padding: '8px 12px', fontSize: '14px', boxSizing: 'border-box' }}
                placeholder="Genre"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: T.textSecondary }}>
                Lead Vocal
              </label>
              <select
                value={editingSong?.vocal_type || ''}
                onChange={e => editingSong && setEditingSong({ ...editingSong, vocal_type: e.target.value })}
                style={{ ...inputStyle, width: '100%', padding: '8px 12px', fontSize: '14px', boxSizing: 'border-box' }}
              >
                {VOCAL_TYPES.map(v => (
                  <option key={v.value} value={v.value}>{v.label}</option>
                ))}
              </select>
              <div style={{ color: T.textMuted, fontSize: '12px', marginTop: '4px' }}>
                Drives the Cowboys / Cowgirls / Duets filter on the public catalogue.
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: T.textSecondary }}>
                Lyrics
              </label>
              <textarea
                value={editingSong?.lyrics || ''}
                onChange={e => editingSong && setEditingSong({ ...editingSong, lyrics: e.target.value })}
                style={{ ...inputStyle, width: '100%', padding: '8px 12px', fontSize: '14px', minHeight: '150px', boxSizing: 'border-box', resize: 'vertical' }}
                placeholder="Song lyrics"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: T.textSecondary }}>
                PDF Path
              </label>
              <input
                type="text"
                value={editingSong?.pdf_path || ''}
                onChange={e => editingSong && setEditingSong({ ...editingSong, pdf_path: e.target.value })}
                style={{ ...inputStyle, width: '100%', padding: '8px 12px', fontSize: '14px', boxSizing: 'border-box' }}
                placeholder="/charts/Artist - Title.pdf"
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleSave}
                style={{
                  ...btnBase,
                  padding: '10px 20px',
                  background: T.btnPrimary,
                  color: '#fff',
                }}
              >
                Save
              </button>
              <button
                onClick={() => {
                  setEditingSong(null);
                  setCreatingSong(false);
                }}
                style={{
                  ...btnBase,
                  padding: '10px 20px',
                  backgroundColor: T.btnSecondary,
                  color: T.text,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── stats panel ── */}
      {viewingStats && songStats && (
        <div style={{
          border: `2px solid ${T.amber}`,
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '20px',
          backgroundColor: T.cardBg,
        }}>
          <h2 style={{ color: T.gold, marginTop: 0 }}>Song Statistics</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '15px',
            marginBottom: '20px',
          }}>
            {[
              { label: 'Total Plays', value: songStats.total_plays },
              { label: 'Completed', value: songStats.completed_plays },
              { label: 'Skipped', value: songStats.skipped_plays },
              { label: 'Expired', value: songStats.expired_plays },
              { label: 'Unique Users', value: songStats.unique_users },
            ].map(({ label, value }) => (
              <div key={label} style={{
                padding: '12px',
                backgroundColor: 'rgba(30,18,10,0.8)',
                borderRadius: '6px',
                border: `1px solid ${T.border}`,
              }}>
                <div style={{ fontSize: '12px', color: T.textSecondary, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {label}
                </div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: T.gold }}>
                  {value}
                </div>
              </div>
            ))}
            <div style={{
              padding: '12px',
              backgroundColor: 'rgba(30,18,10,0.8)',
              borderRadius: '6px',
              border: `1px solid ${T.border}`,
            }}>
              <div style={{ fontSize: '12px', color: T.textSecondary, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Last Played
              </div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: T.text }}>
                {formatDate(songStats.last_played)}
              </div>
            </div>
            <div style={{
              padding: '12px',
              backgroundColor: 'rgba(30,18,10,0.8)',
              borderRadius: '6px',
              border: `1px solid ${T.border}`,
            }}>
              <div style={{ fontSize: '12px', color: T.textSecondary, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                First Queued
              </div>
              <div style={{ fontSize: '16px', fontWeight: 600, color: T.text }}>
                {formatDate(songStats.first_queued)}
              </div>
            </div>
          </div>

          {/* top users table */}
          {songStats.users.length > 0 && (
            <div>
              <h3 style={{ color: T.gold }}>Top Users</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: T.tableHeaderBg }}>
                    <th style={{ padding: '8px', textAlign: 'left', color: T.gold, borderBottom: `1px solid ${T.border}` }}>User</th>
                    <th style={{ padding: '8px', textAlign: 'left', color: T.gold, borderBottom: `1px solid ${T.border}` }}>Plays</th>
                    <th style={{ padding: '8px', textAlign: 'left', color: T.gold, borderBottom: `1px solid ${T.border}` }}>Last Played</th>
                  </tr>
                </thead>
                <tbody>
                  {songStats.users.map((user, idx) => (
                    <tr key={idx} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <td style={{ padding: '8px', color: T.text }}>{user.user_name}</td>
                      <td style={{ padding: '8px', color: T.text }}>{user.play_count}</td>
                      <td style={{ padding: '8px', color: T.textSecondary }}>{formatDate(user.last_played)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Vote Statistics Section */}
          {voteStats && (
            <div style={{ marginTop: '30px', borderTop: `2px solid ${T.border}`, paddingTop: '20px' }}>
              <h3 style={{ color: T.gold }}>Vote Statistics</h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '15px',
                marginBottom: '20px',
              }}>
                {[
                  { label: 'Total Votes', value: voteStats.aggregate.total_votes },
                  { label: 'Unique Voters', value: voteStats.aggregate.unique_voters },
                  { label: 'Comments', value: voteStats.aggregate.comments_count },
                ].map(({ label, value }) => (
                  <div key={label} style={{
                    padding: '12px',
                    backgroundColor: 'rgba(30,18,10,0.8)',
                    borderRadius: '6px',
                    border: `1px solid ${T.border}`,
                  }}>
                    <div style={{ fontSize: '12px', color: T.textSecondary, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {label}
                    </div>
                    <div style={{ fontSize: '24px', fontWeight: 700, color: T.gold }}>
                      {value}
                    </div>
                  </div>
                ))}
                <div style={{
                  padding: '12px',
                  backgroundColor: 'rgba(30,18,10,0.8)',
                  borderRadius: '6px',
                  border: `1px solid ${T.border}`,
                }}>
                  <div style={{ fontSize: '12px', color: T.textSecondary, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Average Rating
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: T.gold }}>
                    {voteStats.aggregate.average_rating > 0
                      ? voteStats.aggregate.average_rating.toFixed(2)
                      : 'No votes yet'}
                  </div>
                </div>
              </div>

              {/* Vote Distribution */}
              {voteStats.aggregate.total_votes > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: T.gold }}>Vote Distribution</h4>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {[5, 4, 3, 2, 1].map(rating => {
                      const count = voteStats.aggregate[`${rating === 5 ? 'five' : rating === 4 ? 'four' : rating === 3 ? 'three' : rating === 2 ? 'two' : 'one'}_star` as keyof typeof voteStats.aggregate] as number;
                      const percentage = voteStats.aggregate.total_votes > 0
                        ? (count / voteStats.aggregate.total_votes * 100).toFixed(1)
                        : '0';
                      return (
                        <div key={rating} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ width: '70px', textAlign: 'right', color: T.textSecondary, fontSize: '13px' }}>
                            {rating} star: {count}
                          </span>
                          <div style={{
                            flex: 1,
                            height: '20px',
                            backgroundColor: 'rgba(30,18,10,0.8)',
                            borderRadius: '4px',
                            overflow: 'hidden',
                            border: `1px solid ${T.border}`,
                          }}>
                            <div style={{
                              width: `${percentage}%`,
                              height: '100%',
                              backgroundColor: rating >= 4 ? T.successText : rating >= 3 ? T.amber : T.btnDanger,
                              transition: 'width 0.3s',
                            }} />
                          </div>
                          <span style={{ width: '40px', fontSize: '12px', color: T.textMuted }}>
                            {percentage}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Monthly Trends */}
              {voteStats.monthlyTrends.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: T.gold }}>Monthly Trends (Last 12 Months)</h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr style={{ backgroundColor: T.tableHeaderBg }}>
                          <th style={{ padding: '8px', textAlign: 'left', color: T.gold, borderBottom: `1px solid ${T.border}` }}>Month</th>
                          <th style={{ padding: '8px', textAlign: 'center', color: T.gold, borderBottom: `1px solid ${T.border}` }}>Votes</th>
                          <th style={{ padding: '8px', textAlign: 'center', color: T.gold, borderBottom: `1px solid ${T.border}` }}>Avg Rating</th>
                        </tr>
                      </thead>
                      <tbody>
                        {voteStats.monthlyTrends.map((trend, idx) => (
                          <tr key={idx} style={{ borderBottom: `1px solid ${T.border}` }}>
                            <td style={{ padding: '8px', color: T.text }}>
                              {new Date(trend.month).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center', color: T.text }}>{trend.vote_count}</td>
                            <td style={{ padding: '8px', textAlign: 'center', color: T.amber }}>
                              {trend.average_rating > 0 ? trend.average_rating.toFixed(2) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Recent Comments */}
              {voteStats.recentComments.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: T.gold }}>Recent Comments</h4>
                  <div style={{ display: 'grid', gap: '10px' }}>
                    {voteStats.recentComments.map((comment, idx) => (
                      <div key={idx} style={{
                        padding: '12px',
                        backgroundColor: 'rgba(30,18,10,0.8)',
                        borderRadius: '6px',
                        borderLeft: `3px solid ${T.amber}`,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <strong style={{ color: T.text }}>{comment.voter_name}</strong>
                          <span style={{ color: T.amber }}>
                            {comment.rating}/5
                          </span>
                        </div>
                        <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: T.textSecondary }}>
                          {comment.comment}
                        </p>
                        <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: T.textMuted }}>
                          {formatDate(comment.vote_date)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={() => handleViewVoteHistory(viewingStats!)}
                style={{
                  ...btnBase,
                  marginTop: '10px',
                  padding: '8px 16px',
                  background: T.btnPrimary,
                  color: '#fff',
                }}
              >
                View Full Vote History
              </button>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
            <button
              onClick={() => {
                setViewingStats(null);
                setSongStats(null);
                setVoteStats(null);
              }}
              style={{
                ...btnBase,
                padding: '8px 16px',
                backgroundColor: T.btnSecondary,
                color: T.text,
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── Vote History Modal ── */}
      {viewingVoteHistory && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: T.overlayBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px',
        }}>
          <div style={{
            backgroundColor: T.cardBg,
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            border: `1px solid ${T.border}`,
          }}>
            <h2 style={{ color: T.gold, marginTop: 0 }}>Vote History</h2>

            {/* Filters */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '10px',
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: 'rgba(30,18,10,0.8)',
              borderRadius: '6px',
              border: `1px solid ${T.border}`,
            }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: 'bold', color: T.textSecondary }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={voteHistoryFilters.startDate}
                  onChange={e => {
                    setVoteHistoryFilters({ ...voteHistoryFilters, startDate: e.target.value });
                    fetchVoteHistory(viewingVoteHistory, 0);
                  }}
                  style={{ ...inputStyle, width: '100%', padding: '5px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: 'bold', color: T.textSecondary }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={voteHistoryFilters.endDate}
                  onChange={e => {
                    setVoteHistoryFilters({ ...voteHistoryFilters, endDate: e.target.value });
                    fetchVoteHistory(viewingVoteHistory, 0);
                  }}
                  style={{ ...inputStyle, width: '100%', padding: '5px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: 'bold', color: T.textSecondary }}>
                  Min Rating
                </label>
                <select
                  value={voteHistoryFilters.minRating}
                  onChange={e => {
                    setVoteHistoryFilters({ ...voteHistoryFilters, minRating: e.target.value });
                    fetchVoteHistory(viewingVoteHistory, 0);
                  }}
                  style={{ ...inputStyle, width: '100%', padding: '5px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                >
                  <option value="">Any</option>
                  {[1, 2, 3, 4, 5].map(r => <option key={r} value={r}>{r} star</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: 'bold', color: T.textSecondary }}>
                  Max Rating
                </label>
                <select
                  value={voteHistoryFilters.maxRating}
                  onChange={e => {
                    setVoteHistoryFilters({ ...voteHistoryFilters, maxRating: e.target.value });
                    fetchVoteHistory(viewingVoteHistory, 0);
                  }}
                  style={{ ...inputStyle, width: '100%', padding: '5px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                >
                  <option value="">Any</option>
                  {[1, 2, 3, 4, 5].map(r => <option key={r} value={r}>{r} star</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: 'bold', color: T.textSecondary }}>
                  Search
                </label>
                <input
                  type="text"
                  value={voteHistoryFilters.search}
                  onChange={e => setVoteHistoryFilters({ ...voteHistoryFilters, search: e.target.value })}
                  onKeyPress={e => {
                    if (e.key === 'Enter') {
                      fetchVoteHistory(viewingVoteHistory, 0);
                    }
                  }}
                  placeholder="Voter name or comment"
                  style={{ ...inputStyle, width: '100%', padding: '5px 8px', fontSize: '12px', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', color: T.textSecondary }}>
                  <input
                    type="checkbox"
                    checked={voteHistoryFilters.hasComment}
                    onChange={e => {
                      setVoteHistoryFilters({ ...voteHistoryFilters, hasComment: e.target.checked });
                      fetchVoteHistory(viewingVoteHistory, 0);
                    }}
                    style={{ accentColor: T.amber }}
                  />
                  <span style={{ fontSize: '12px' }}>Has Comment</span>
                </label>
              </div>
            </div>

            {voteHistoryLoading ? (
              <p style={{ color: T.textSecondary }}>Loading vote history...</p>
            ) : voteHistory.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '20px', color: T.textMuted }}>
                No votes found matching your criteria.
              </p>
            ) : (
              <>
                <div style={{ overflowX: 'auto', marginBottom: '15px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ backgroundColor: T.tableHeaderBg }}>
                        <th style={{ padding: '10px', textAlign: 'left', color: T.gold, borderBottom: `1px solid ${T.border}` }}>Voter</th>
                        <th style={{ padding: '10px', textAlign: 'center', color: T.gold, borderBottom: `1px solid ${T.border}` }}>Rating</th>
                        <th style={{ padding: '10px', textAlign: 'left', color: T.gold, borderBottom: `1px solid ${T.border}` }}>Comment</th>
                        <th style={{ padding: '10px', textAlign: 'left', color: T.gold, borderBottom: `1px solid ${T.border}` }}>Performer</th>
                        <th style={{ padding: '10px', textAlign: 'left', color: T.gold, borderBottom: `1px solid ${T.border}` }}>Date</th>
                        <th style={{ padding: '10px', textAlign: 'left', color: T.gold, borderBottom: `1px solid ${T.border}` }}>Gig Session</th>
                      </tr>
                    </thead>
                    <tbody>
                      {voteHistory.map((vote, idx) => (
                        <tr key={vote.id} style={{
                          backgroundColor: idx % 2 === 0 ? T.tableRowEven : T.tableRowOdd,
                          borderBottom: `1px solid ${T.border}`,
                        }}>
                          <td style={{ padding: '10px', color: T.text }}>{vote.voter_name}</td>
                          <td style={{ padding: '10px', textAlign: 'center', color: T.amber, fontWeight: 600 }}>
                            {vote.rating}/5
                          </td>
                          <td style={{ padding: '10px', maxWidth: '300px', color: T.text }}>
                            {vote.comment || <span style={{ color: T.textMuted, fontStyle: 'italic' }}>No comment</span>}
                          </td>
                          <td style={{ padding: '10px', color: T.textSecondary }}>{vote.performer_name || '-'}</td>
                          <td style={{ padding: '10px', color: T.textSecondary }}>{formatDate(vote.performance_date || vote.created_at)}</td>
                          <td style={{ padding: '10px', color: T.textSecondary }}>{vote.gig_session || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {voteHistoryTotal > 50 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px' }}>
                    <div style={{ fontSize: '14px', color: T.textMuted }}>
                      Showing {voteHistoryOffset + 1}-{Math.min(voteHistoryOffset + 50, voteHistoryTotal)} of {voteHistoryTotal} votes
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={() => fetchVoteHistory(viewingVoteHistory, Math.max(0, voteHistoryOffset - 50))}
                        disabled={voteHistoryOffset === 0 || voteHistoryLoading}
                        style={{
                          ...btnBase,
                          padding: '6px 12px',
                          background: voteHistoryOffset === 0 ? T.btnSecondary : T.btnPrimary,
                          color: voteHistoryOffset === 0 ? T.textMuted : '#fff',
                          cursor: voteHistoryOffset === 0 ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => fetchVoteHistory(viewingVoteHistory, voteHistoryOffset + 50)}
                        disabled={voteHistoryOffset + 50 >= voteHistoryTotal || voteHistoryLoading}
                        style={{
                          ...btnBase,
                          padding: '6px 12px',
                          background: voteHistoryOffset + 50 >= voteHistoryTotal ? T.btnSecondary : T.btnPrimary,
                          color: voteHistoryOffset + 50 >= voteHistoryTotal ? T.textMuted : '#fff',
                          cursor: voteHistoryOffset + 50 >= voteHistoryTotal ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            <button
              onClick={() => {
                setViewingVoteHistory(null);
                setVoteHistory([]);
                setVoteHistoryFilters({
                  startDate: '',
                  endDate: '',
                  minRating: '',
                  maxRating: '',
                  hasComment: false,
                  search: ''
                });
              }}
              style={{
                ...btnBase,
                marginTop: '15px',
                padding: '8px 16px',
                backgroundColor: T.btnSecondary,
                color: T.text,
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── chart viewer ── */}
      {viewingChart && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            marginBottom: '16px',
          }}>
            <button
              onClick={() => setViewingChart(null)}
              style={{
                ...btnBase,
                padding: '8px 16px',
                backgroundColor: T.btnSecondary,
                color: T.text,
                fontSize: '14px',
                border: `1px solid ${T.border}`,
              }}
            >
              Back to Song List
            </button>
            <div>
              <span style={{ color: T.gold, fontWeight: 700, fontSize: '18px' }}>
                {viewingChart.title}
              </span>
              <span style={{ color: T.textSecondary, marginLeft: '10px' }}>
                {viewingChart.artist}
              </span>
              <span style={{ color: T.amber, marginLeft: '10px', fontSize: '14px' }}>
                ({INSTRUMENTS.find(i => i.value === viewingChart.instrument)?.label || viewingChart.instrument})
              </span>
            </div>
          </div>
          <iframe
            src={apiUrl(`/charts/${viewingChart.slug}_${viewingChart.instrument}.pdf?v=${viewingChart.v}`)}
            style={{
              width: '100%',
              height: 'calc(100vh - 200px)',
              border: `1px solid ${T.border}`,
              borderRadius: '6px',
              backgroundColor: '#fff',
            }}
            title={`${viewingChart.title} - ${viewingChart.instrument} chart`}
          />
        </div>
      )}

      {/* ── lyrics editor ── */}
      {editingLyrics && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            marginBottom: '16px',
          }}>
            <button
              onClick={() => setEditingLyrics(null)}
              style={{
                ...btnBase,
                padding: '8px 16px',
                backgroundColor: T.btnSecondary,
                color: T.text,
                fontSize: '14px',
                border: `1px solid ${T.border}`,
              }}
            >
              Back to Song List
            </button>
            <div>
              <span style={{ color: T.gold, fontWeight: 700, fontSize: '18px' }}>
                {editingLyrics.song.title}
              </span>
              <span style={{ color: T.textSecondary, marginLeft: '10px' }}>
                {editingLyrics.song.artist}
              </span>
              <span style={{ color: T.amber, marginLeft: '10px', fontSize: '14px' }}>
                (Edit Lyrics)
              </span>
            </div>
            {(editingLyrics.song as any).available_charts?.length > 0 && (
              <select
                value={editingLyrics.referenceInstrument || ''}
                onChange={e => setEditingLyrics(prev =>
                  prev ? { ...prev, referenceInstrument: e.target.value || null } : null
                )}
                style={{ ...inputStyle, padding: '5px 8px', fontSize: '13px', minWidth: '160px' }}
              >
                <option value="">Reference chart...</option>
                {INSTRUMENTS.filter(inst => (editingLyrics.song as any).available_charts.includes(inst.value)).map(inst => (
                  <option key={inst.value} value={inst.value}>{inst.label}</option>
                ))}
              </select>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
              <button
                onClick={handleSaveLyricsAndRegenerate}
                disabled={editingLyrics.saving || editingLyrics.regenerating}
                style={{
                  ...btnBase,
                  padding: '8px 16px',
                  background: T.btnPrimary,
                  color: '#fff',
                  fontSize: '13px',
                  opacity: (editingLyrics.saving || editingLyrics.regenerating) ? 0.6 : 1,
                }}
              >
                {editingLyrics.saving
                  ? 'Saving...'
                  : editingLyrics.regenerating
                  ? 'Regenerating...'
                  : 'Save & Regenerate PDF'}
              </button>
              <button
                onClick={handleSaveLyricsOnly}
                disabled={editingLyrics.saving || editingLyrics.regenerating}
                style={{
                  ...btnBase,
                  padding: '8px 16px',
                  backgroundColor: T.btnSecondary,
                  color: T.text,
                  fontSize: '13px',
                  border: `1px solid ${T.border}`,
                }}
              >
                Save Only
              </button>
              <button
                onClick={() => window.open(apiUrl(`/charts/${(editingLyrics.song as any).slug}_vocals.pdf?v=${Date.now()}`), '_blank')}
                style={{
                  ...btnBase,
                  padding: '8px 16px',
                  backgroundColor: T.btnSecondary,
                  color: T.gold,
                  fontSize: '13px',
                  border: `1px solid ${T.border}`,
                }}
              >
                Preview PDF
              </button>
              <button
                onClick={handleToggleLyricsValidated}
                style={{
                  ...btnBase,
                  padding: '8px 16px',
                  backgroundColor: editingLyrics.song.lyrics_validated ? T.successText : T.btnSecondary,
                  color: editingLyrics.song.lyrics_validated ? '#fff' : T.textSecondary,
                  fontSize: '13px',
                  border: `1px solid ${editingLyrics.song.lyrics_validated ? T.successText : T.border}`,
                }}
              >
                {editingLyrics.song.lyrics_validated ? 'Validated' : 'Mark Validated'}
              </button>
              {editingLyrics.justSaved && (
                <>
                  <style>{`@keyframes savedPop{0%{transform:scale(0.6);opacity:0}55%{transform:scale(1.12);opacity:1}100%{transform:scale(1);opacity:1}}`}</style>
                  <span
                    role="status"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '7px 14px',
                      borderRadius: '999px',
                      backgroundColor: 'rgba(46, 204, 113, 0.16)',
                      color: T.successText,
                      fontSize: '13px',
                      fontWeight: 700,
                      transformOrigin: 'left center',
                      animation: 'savedPop 0.35s ease-out',
                    }}
                  >
                    💾 Saved!
                  </span>
                </>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: editingLyrics.referenceInstrument ? '1fr 1fr' : '1fr', gap: '20px' }}>
            {editingLyrics.referenceInstrument && (
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 'bold',
                  color: T.textSecondary,
                  fontSize: '14px',
                }}>
                  {INSTRUMENTS.find(i => i.value === editingLyrics.referenceInstrument)?.label || editingLyrics.referenceInstrument}
                </label>
                <iframe
                  src={apiUrl(`/charts/${(editingLyrics.song as any).slug}_${editingLyrics.referenceInstrument}.pdf`)}
                  style={{
                    width: '100%',
                    height: 'calc(100vh - 250px)',
                    border: `1px solid ${T.border}`,
                    borderRadius: '6px',
                    backgroundColor: '#fff',
                  }}
                  title={`${editingLyrics.song.title} - ${editingLyrics.referenceInstrument} chart`}
                />
              </div>
            )}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: 'bold',
                color: T.textSecondary,
                fontSize: '14px',
              }}>
                Lyrics (use [Chorus], [Verse], [Bridge] for section markers)
              </label>
              <textarea
                value={editingLyrics.lyrics}
                onChange={e => setEditingLyrics(prev =>
                  prev ? { ...prev, lyrics: e.target.value } : null
                )}
                style={{
                  ...inputStyle,
                  width: '100%',
                  height: 'calc(100vh - 250px)',
                  padding: '16px',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  lineHeight: '1.6',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
                placeholder="Enter lyrics here..."
              />
            </div>
          </div>
        </div>
      )}

      {/* ── main song table ── */}
      {!viewingChart && !editingLyrics && (<><div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: T.tableHeaderBg }}>
              <th style={{ padding: '12px', textAlign: 'left', color: T.gold, borderBottom: `2px solid ${T.border}` }}>Title</th>
              <th style={{ padding: '12px', textAlign: 'left', color: T.gold, borderBottom: `2px solid ${T.border}` }}>Artist</th>
              <th style={{ padding: '12px', textAlign: 'left', color: T.gold, borderBottom: `2px solid ${T.border}` }}>Genre</th>
              {showStats && (
                <>
                  <th style={{ padding: '12px', textAlign: 'center', color: T.gold, borderBottom: `2px solid ${T.border}` }}>Plays</th>
                  <th style={{ padding: '12px', textAlign: 'center', color: T.gold, borderBottom: `2px solid ${T.border}` }}>Completed</th>
                  <th style={{ padding: '12px', textAlign: 'center', color: T.gold, borderBottom: `2px solid ${T.border}` }}>Users</th>
                  <th style={{ padding: '12px', textAlign: 'center', color: T.gold, borderBottom: `2px solid ${T.border}` }}>Votes</th>
                  <th style={{ padding: '12px', textAlign: 'center', color: T.gold, borderBottom: `2px solid ${T.border}` }}>Rating</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: T.gold, borderBottom: `2px solid ${T.border}` }}>Last Played</th>
                </>
              )}
              <th style={{ padding: '12px', textAlign: 'center', color: T.gold, borderBottom: `2px solid ${T.border}` }}>Charts</th>
              <th style={{ padding: '12px', textAlign: 'center', color: T.gold, borderBottom: `2px solid ${T.border}` }}>Lyrics</th>
              <th style={{ padding: '12px', textAlign: 'center', color: T.gold, borderBottom: `2px solid ${T.border}` }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSongs.map((song, idx) => (
              <tr key={song.id} style={{
                backgroundColor: idx % 2 === 0 ? T.tableRowEven : T.tableRowOdd,
                borderBottom: `1px solid ${T.border}`,
              }}>
                <td style={{ padding: '10px', color: T.text, fontWeight: 500 }}>{song.title}</td>
                <td style={{ padding: '10px', color: T.textSecondary }}>{song.artist}</td>
                <td style={{ padding: '10px' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    backgroundColor: T.genreBadgeBg,
                    color: T.genreBadgeText,
                  }}>
                    {song.genre}
                  </span>
                </td>
                {showStats && (
                  <>
                    <td style={{ padding: '10px', textAlign: 'center', color: T.text }}>
                      {song.total_plays || 0}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center', color: T.text }}>
                      {song.completed_plays || 0}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center', color: T.text }}>
                      {song.unique_users || 0}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center', color: T.text }}>
                      {song.votes?.total_votes || 0}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center', color: T.amber, fontWeight: 600 }}>
                      {song.votes?.average_rating
                        ? song.votes.average_rating.toFixed(1)
                        : '-'}
                    </td>
                    <td style={{ padding: '10px', color: T.textSecondary }}>
                      {formatDate(song.last_played)}
                    </td>
                  </>
                )}
                <td style={{ padding: '10px', textAlign: 'center' }}>
                  {(song as any).chart_count > 0 ? (
                    <span style={{ color: (song as any).chart_count >= 6 ? T.successText : T.amber, fontWeight: 700 }}>
                      {(song as any).chart_count}
                    </span>
                  ) : (song as any).has_full ? (
                    <span style={{ color: T.successText, fontWeight: 700 }}>Full</span>
                  ) : (
                    <span style={{ color: T.btnDanger, fontWeight: 700 }}>0</span>
                  )}
                </td>
                <td style={{ padding: '10px', textAlign: 'center' }}>
                  {(song as any).has_lyrics ? (
                    <span style={{ color: song.lyrics_validated ? T.successText : T.amber, fontWeight: 700 }}>
                      Y
                    </span>
                  ) : (
                    <span style={{ color: T.btnDanger, fontWeight: 700 }}>N</span>
                  )}
                </td>
                <td style={{ padding: '10px' }}>
                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleEdit(song)}
                      style={{
                        ...btnBase,
                        padding: '5px 10px',
                        background: T.btnPrimary,
                        color: '#fff',
                        fontSize: '12px',
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleEditLyrics(song)}
                      style={{
                        ...btnBase,
                        padding: '5px 10px',
                        backgroundColor: (song as any).has_lyrics ? T.btnSecondary : T.amber,
                        color: (song as any).has_lyrics ? T.gold : '#fff',
                        fontSize: '12px',
                        border: (song as any).has_lyrics ? `1px solid ${T.border}` : 'none',
                      }}
                    >
                      {(song as any).has_lyrics ? 'Edit Lyrics' : 'Add Lyrics'}
                    </button>
                    {((song as any).available_charts?.length > 0 || (song as any).has_full) && (
                      chartDropdownSongId === song.id ? (
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                          <select
                            autoFocus
                            defaultValue=""
                            onChange={e => {
                              if (e.target.value) {
                                setViewingChart({
                                  songId: song.id,
                                  title: song.title,
                                  artist: song.artist,
                                  slug: (song as any).slug,
                                  instrument: e.target.value,
                                  v: Date.now(),
                                });
                                setChartDropdownSongId(null);
                              }
                            }}
                            onBlur={() => setChartDropdownSongId(null)}
                            style={{ ...inputStyle, padding: '3px 5px', fontSize: '11px' }}
                          >
                            <option value="" disabled>Select instrument...</option>
                            {INSTRUMENTS.filter(inst =>
                              (song as any).available_charts?.includes(inst.value) ||
                              (inst.value === 'full' && (song as any).has_full)
                            ).map(inst => (
                              <option key={inst.value} value={inst.value}>{inst.label}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => setChartDropdownSongId(null)}
                            style={{
                              ...btnBase,
                              padding: '5px 8px',
                              backgroundColor: T.btnSecondary,
                              color: T.text,
                              fontSize: '11px',
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setChartDropdownSongId(song.id)}
                          style={{
                            ...btnBase,
                            padding: '5px 10px',
                            backgroundColor: T.btnSecondary,
                            color: T.gold,
                            fontSize: '12px',
                            border: `1px solid ${T.border}`,
                          }}
                        >
                          View Chart
                        </button>
                      )
                    )}
                    {uploadingInstrument?.songId === song.id ? (
                      <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                        <select
                          value={uploadingInstrument.instrument}
                          onChange={e => setUploadingInstrument({ songId: song.id, instrument: e.target.value })}
                          style={{ ...inputStyle, padding: '3px 5px', fontSize: '11px' }}
                        >
                          {INSTRUMENTS.map(inst => (
                            <option key={inst.value} value={inst.value}>{inst.label}</option>
                          ))}
                        </select>
                        <label style={{
                          ...btnBase,
                          padding: '5px 10px',
                          backgroundColor: T.amber,
                          color: '#fff',
                          fontSize: '12px',
                          display: 'inline-block',
                        }}>
                          {uploadingChart === song.id ? 'Uploading...' : 'Choose File'}
                          <input
                            type="file"
                            accept=".pdf"
                            style={{ display: 'none' }}
                            onChange={e => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleChartUpload(song.id, file, uploadingInstrument.instrument);
                              }
                            }}
                            disabled={uploadingChart === song.id}
                          />
                        </label>
                        <button
                          onClick={() => setUploadingInstrument(null)}
                          style={{
                            ...btnBase,
                            padding: '5px 8px',
                            backgroundColor: T.btnSecondary,
                            color: T.text,
                            fontSize: '11px',
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setUploadingInstrument({ songId: song.id, instrument: 'bass' })}
                        style={{
                          ...btnBase,
                          padding: '5px 10px',
                          backgroundColor: T.amber,
                          color: '#fff',
                          fontSize: '12px',
                        }}
                        disabled={uploadingChart === song.id}
                      >
                        Upload Chart
                      </button>
                    )}
                    <button
                      onClick={() => handleViewStats(song.id)}
                      style={{
                        ...btnBase,
                        padding: '5px 10px',
                        backgroundColor: T.btnSecondary,
                        color: T.gold,
                        fontSize: '12px',
                        border: `1px solid ${T.border}`,
                      }}
                    >
                      Stats
                    </button>
                    <button
                      onClick={() => handleDelete(song.id)}
                      style={{
                        ...btnBase,
                        padding: '5px 10px',
                        backgroundColor: T.btnDanger,
                        color: '#fff',
                        fontSize: '12px',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredSongs.length === 0 && !loading && (
        <p style={{ textAlign: 'center', padding: '20px', color: T.textMuted }}>
          No songs found matching your search criteria.
        </p>
      )}
      </>)}

      {/* ── validation report modal ── */}
      {validationReport && (
        <div
          onClick={() => setValidationReport(null)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: T.overlayBg,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            paddingTop: '40px',
            zIndex: 1000,
            overflow: 'auto',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: '#1a0f0a',
              border: `2px solid ${T.amber}`,
              borderRadius: '12px',
              padding: '32px',
              maxWidth: '800px',
              width: '90%',
              maxHeight: '85vh',
              overflow: 'auto',
              marginBottom: '40px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ color: T.gold, margin: 0 }}>Data Validation Report</h2>
              <button
                onClick={() => setValidationReport(null)}
                style={{ ...btnBase, background: 'none', border: 'none', color: T.textSecondary, fontSize: '24px' }}
              >
                x
              </button>
            </div>

            {/* Summary cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
              <div style={{ backgroundColor: T.cardBg, borderRadius: '8px', padding: '20px', border: `1px solid ${T.border}` }}>
                <h3 style={{ color: T.amber, margin: '0 0 12px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Chart Coverage
                </h3>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: T.gold }}>
                  {validationReport.charts.withAllCharts + validationReport.charts.withSomeCharts + (validationReport.charts.withFullChartOnly || 0)}/{validationReport.totalSongs}
                </div>
                <div style={{ color: T.textSecondary, fontSize: '13px', marginTop: '4px' }}>
                  songs have charts
                </div>
                <div style={{ marginTop: '12px', fontSize: '13px', color: T.textMuted }}>
                  Complete: {validationReport.charts.withAllCharts} | Partial: {validationReport.charts.withSomeCharts} | Full band only: {validationReport.charts.withFullChartOnly || 0} | None: {validationReport.charts.withNoCharts}
                </div>
              </div>
              <div style={{ backgroundColor: T.cardBg, borderRadius: '8px', padding: '20px', border: `1px solid ${T.border}` }}>
                <h3 style={{ color: T.amber, margin: '0 0 12px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Lyrics Coverage
                </h3>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: T.gold }}>
                  {validationReport.lyrics.withLyrics}/{validationReport.totalSongs}
                </div>
                <div style={{ color: T.textSecondary, fontSize: '13px', marginTop: '4px' }}>
                  songs have lyrics
                </div>
                <div style={{ marginTop: '12px', fontSize: '13px', color: T.textMuted }}>
                  Missing: {validationReport.lyrics.missingLyrics}
                </div>
              </div>
            </div>

            {/* Per-instrument breakdown */}
            <div style={{ backgroundColor: T.cardBg, borderRadius: '8px', padding: '20px', border: `1px solid ${T.border}`, marginBottom: '24px' }}>
              <h3 style={{ color: T.amber, margin: '0 0 12px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Charts by Instrument
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
                {Object.entries(validationReport.charts.byInstrument).map(([inst, count]) => {
                  const pct = validationReport.totalSongs > 0 ? Math.round((count as number / validationReport.totalSongs) * 100) : 0;
                  return (
                    <div key={inst} style={{ padding: '10px', backgroundColor: '#1e1008', borderRadius: '6px' }}>
                      <div style={{ fontSize: '12px', color: T.textSecondary, textTransform: 'uppercase', marginBottom: '4px' }}>{inst}</div>
                      <div style={{ fontSize: '1.2rem', fontWeight: 700, color: pct === 100 ? T.successText : pct > 0 ? T.text : T.textMuted }}>
                        {count as number}/{validationReport.totalSongs}
                      </div>
                      <div style={{
                        marginTop: '6px',
                        height: '4px',
                        backgroundColor: '#3a2510',
                        borderRadius: '2px',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${pct}%`,
                          height: '100%',
                          backgroundColor: pct === 100 ? T.successText : T.amber,
                          borderRadius: '2px',
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Songs missing lyrics */}
            {validationReport.songs.filter((s: any) => !s.hasLyrics).length > 0 && (
              <div style={{ backgroundColor: T.cardBg, borderRadius: '8px', padding: '20px', border: `1px solid ${T.border}`, marginBottom: '24px' }}>
                <h3 style={{ color: T.amber, margin: '0 0 12px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Songs Missing Lyrics ({validationReport.songs.filter((s: any) => !s.hasLyrics).length})
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {validationReport.songs.filter((s: any) => !s.hasLyrics).map((s: any) => (
                    <div key={s.id} style={{ fontSize: '13px', color: T.textSecondary }}>
                      {s.title} - <span style={{ color: T.textMuted }}>{s.artist}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Songs with incomplete charts */}
            {validationReport.songs.filter((s: any) => s.chartsPresent.length > 0 && s.chartsMissing.length > 0).length > 0 && (
              <div style={{ backgroundColor: T.cardBg, borderRadius: '8px', padding: '20px', border: `1px solid ${T.border}`, marginBottom: '24px' }}>
                <h3 style={{ color: T.amber, margin: '0 0 12px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Incomplete Chart Sets
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {validationReport.songs.filter((s: any) => s.chartsPresent.length > 0 && s.chartsMissing.length > 0).map((s: any) => (
                    <div key={s.id} style={{ fontSize: '13px' }}>
                      <span style={{ color: T.text }}>{s.title}</span>
                      <span style={{ color: T.textMuted }}> - {s.artist}</span>
                      <div style={{ color: T.errorText, fontSize: '12px', marginTop: '2px' }}>
                        Missing: {s.chartsMissing.join(', ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Songs covered only by a full-band chart */}
            {validationReport.charts.withFullChartOnly > 0 && (
              <div style={{ backgroundColor: T.cardBg, borderRadius: '8px', padding: '20px', border: `1px solid ${T.border}`, marginBottom: '24px' }}>
                <h3 style={{ color: T.amber, margin: '0 0 4px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Full Band Chart Only ({validationReport.charts.withFullChartOnly})
                </h3>
                <div style={{ color: T.textMuted, fontSize: '12px', marginBottom: '12px' }}>
                  Covered by a single whole-band sheet - no individual instrument parts yet.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {validationReport.songs
                    .filter((s: any) => s.corePresent?.length === 0 && s.hasFullChart)
                    .map((s: any) => (
                      <div key={s.id} style={{ fontSize: '13px', color: T.textSecondary }}>
                        {s.title} - <span style={{ color: T.textMuted }}>{s.artist}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Songs with no charts */}
            {validationReport.charts.withNoCharts > 0 && (
              <div style={{ backgroundColor: T.cardBg, borderRadius: '8px', padding: '20px', border: `1px solid ${T.border}` }}>
                <h3 style={{ color: T.amber, margin: '0 0 12px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Songs With No Charts ({validationReport.charts.withNoCharts})
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {validationReport.songs.filter((s: any) => s.chartsPresent.length === 0).map((s: any) => (
                    <div key={s.id} style={{ fontSize: '13px', color: T.textSecondary }}>
                      {s.title} - <span style={{ color: T.textMuted }}>{s.artist}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SongAdminPage;
